import React from 'react';
import { Flex, Text, Link, Dialog, DialogBody, Skeleton, Box } from '@backstage/ui';
import {
  SearchContextProvider,
  useSearch,
} from '@backstage/plugin-search-react';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { RiSearchLine } from '@remixicon/react';
import { appTranslationRef } from '../../../../core/i18n';
import { SearchBar } from '../search-bar';
import { SearchResultItem } from '../search-result-item';
import styles from './search-modal.module.css';

const MAX_RESULTS = 5;

const SearchModalContent = ({ onClose }: { onClose: () => void }) => {
  const { result } = useSearch();
  const { t } = useTranslationRef(appTranslationRef);

  const results = result.value?.results?.slice(0, MAX_RESULTS) ?? [];
  const hasResults = results.length > 0;
  const isLoading = result.loading;

  return (
    <Flex direction="column" gap="3" className={styles.fullHeight}>
      <div className={styles.flexNone}>
        {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
        <SearchBar placeholder={t('search.modal.placeholder')} autoFocus />
      </div>

      <div className={styles.scrollArea}>
        {isLoading && (
          <Flex direction="column" gap="2">
            {Array.from({ length: MAX_RESULTS }).map((_, i) => (
              <Skeleton key={i} height={68} className={styles.skeletonRounded} />
            ))}
          </Flex>
        )}

        {!isLoading && hasResults && (
          <Flex direction="column" gap="2">
            {results.map((item, index) => (
              <SearchResultItem
                key={`${item.document.location}-${index}`}
                title={item.document.title}
                text={item.document.text}
                location={item.document.location}
                kind={
                  (item.document as unknown as Record<string, string>).kind
                }
                type={item.type}
                onClick={onClose}
              />
            ))}
          </Flex>
        )}

        {!isLoading && !hasResults && (
          <Flex align="center" justify="center" className={styles.noResultsCenter}>
            <Text variant="body-small" color="secondary">
              {t('search.noResults')}
            </Text>
          </Flex>
        )}
      </div>

      <div className={styles.viewAllLink}>
        <Link href="/search" onClick={onClose}>
          <Text variant="body-small" className={styles.viewAllText}>
            {t('search.viewAllResults')}
          </Text>
        </Link>
      </div>
    </Flex>
  );
};

const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
const shortcutLabel = isMac ? '\u2318K' : 'Ctrl+K';

export const SearchModal = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { t } = useTranslationRef(appTranslationRef);

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Workaround: @backstage/ui Dialog applies full-viewport styles to the
  // ModalContent element, so React Aria's useInteractOutside never sees
  // clicks as "outside". We manually close on backdrop clicks instead.
  React.useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[role="dialog"]')) {
        setIsOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isOpen]);

  return (
    <>
      <Box width="100%" p="0 1rem">
        <button
          onClick={() => setIsOpen(true)}
          className={styles.triggerButton}
        >
          <RiSearchLine size={15} />
          <span className={styles.triggerLabel}>
            {t('sidebar.search')}...
          </span>
          <kbd className={styles.kbd}>
            {shortcutLabel}
          </kbd>
        </button>
      </Box>

      <Dialog
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        width="600px"
        height="500px"
      >
        <DialogBody>
          <SearchContextProvider initialState={{ term: '', types: ['software-catalog'], filters: {} }}>
            <SearchModalContent onClose={() => setIsOpen(false)} />
          </SearchContextProvider>
        </DialogBody>
      </Dialog>
    </>
  );
};
